import { WebPubSubClient } from '@azure/web-pubsub-client';
import Axios from 'axios';
import { DIGITAL_DISPATCH_ALL_DISPATCH_POST_APPROVAL_EVENTS_GROUP, DIGITAL_DISPATCH_UPDATES_GROUP_PREFIX } from 'src/constants/digitalDispatchWebPubSub';

/**
 * @typedef {import('@azure/web-pubsub-client').WebPubSubResult} WebPubSubResult
 */

class DigitalDispatchWebPubSubService {
  /**
   * @type {import('@azure/web-pubsub-client').WebPubSubClient}
   */
  #webPubSubClient;

  /**
   * @type {string}
   * Group to join to listen for digital dispatch updates
   * for one particular dispatch
   */
  #digitalDispatchDispatchUpdateGroup;

  /**
   * @type {string}
   */
  #clientAccessUri;

  /**
   * Makes call to GET /get-web-pub-sub-towmagic-client-access-uri
   * on the nsd-api-third-party-integrations API to get a client
   * access token (10 minute expiry) and client access URL
   * (token is already appended on the returned URI) to connect
   * to WebPubSub
   * @returns {Promise<void>}
   */
  async setWebPubSubClientAccessUri() {
    const response = await Axios.get(
      `${process.env.REACT_APP_THIRD_PARTY_INTEGRATIONS_API}/get-web-pub-sub-towmagic-client-access-uri`,
      {
        params: {
          groupName: this.#digitalDispatchDispatchUpdateGroup
        },
        headers: {
          'Ocp-Apim-Subscription-Key': process.env.REACT_APP_NSD_API_KEY
        }
      }
    );
    this.#clientAccessUri = response?.data?.url;
  }

  /**
   * Fetches the Client access token + URI if not set
   * and instantiates a new instance of WebPubSubClient for
   * listening to updates pertaining to a particular dispatch
   * @param {string | number} referenceId
   */
  async setWebPubSubClientToListenToOneDispatch(referenceId) {
    this.generateWebPubSubGroupNameForOneDispatch(referenceId);
    await this.setWebPubSubClientAccessUri();
    this.#webPubSubClient = new WebPubSubClient(this.#clientAccessUri);
  }

  /**
   * Fetches the Client access token + URI if not set
   * and instantiates a new instance of WebPubSubClient for
   * listening to Post Approval Events for all DigitalDispatch dispatches
   * @param {string | number} referenceId
   */
  async setWebPubSubClientToListenToAllPostApprovalEvents() {
    this.generateWebPubSubGroupNameForAllDispatchPostApprovalEvents();
    await this.setWebPubSubClientAccessUri();
    this.#webPubSubClient = new WebPubSubClient(this.#clientAccessUri);
  }

  /**
   * @returns {Promise<void>}
   */
  async startClient() {
    return this.#webPubSubClient.start();
  }

  /**
   * @returns {Promise<void>}
   */
  async stopClient() {
    return this.#webPubSubClient.stop();
  }

  /**
   * Joins the WebPubSub group created by the specified
   * referenceId
   * @returns {Promise<WebPubSubResult>}
   */
  async joinGroup() {
    return this.#webPubSubClient.joinGroup(this.#digitalDispatchDispatchUpdateGroup);
  }

  /**
   * Generates and sets instance value for WebPubSub group name;
   * used to relay towmagic updates about 1 particular dispatch
   * @param {string | number} referenceId
   * @returns {void}
   */
  generateWebPubSubGroupNameForOneDispatch(referenceId) {
    this.#digitalDispatchDispatchUpdateGroup = `${DIGITAL_DISPATCH_UPDATES_GROUP_PREFIX}${referenceId}`;
  }

  /**
   * Generates and sets instance value for WebPubSub group name;
   * used to relay towmagic updates about 1 particular dispatch
   * @param {string | number} referenceId
   * @returns {void}
   */
  generateWebPubSubGroupNameForAllDispatchPostApprovalEvents() {
    this.#digitalDispatchDispatchUpdateGroup = DIGITAL_DISPATCH_ALL_DISPATCH_POST_APPROVAL_EVENTS_GROUP;
  }

  /**
   * Takes in a setState callback for recording dispatch
   * status updates and passes the WebPubSub message payload
   * as an arguement to the callback. On the payload there is a
   * property called 'updateType' which is set by the API and denotes
   * which group this dispatch update belongs to
   * ('postapproval', 'dispatchstatus' , 'locationupdate').
   * Also used for listening to group updates for all dispatch
   * post approval events
   * @param {function} dispatchUpdateCallback
   * @returns {void}
   */
  listenForGroupMessages(dispatchUpdateCallback) {
    this.#webPubSubClient.on('group-message', (e) => {
      dispatchUpdateCallback(JSON.parse(e?.message?.data));
    });
  }

  /**
   * @param {function} callback - Callback to run when
   * the WebPubSubClient successfully connects
   * @returns {void}
   */
  onConnected(callback) {
    this.#webPubSubClient.on('connected', callback);
  }

  /**
   * @param {function} callback - Callback to run
   * when the WebPubSubClient disconnects
   * @returns {void}
   */
  onDisconnected(callback) {
    this.#webPubSubClient.on('disconnected', callback);
  }

  /**
   * @param {function} callback - Callback to run
   * when the WebPubSubClient is stopped
   * @returns {void}
   */
  onStopped(callback) {
    this.#webPubSubClient.on('stopped', callback);
  }
}

export default DigitalDispatchWebPubSubService;
